import React from "react";
import { useTranslation } from "react-i18next";

import { useGroupsAndWorkspaceMembers, useGroupsByMemberEmail } from "@dashboard/groups/store/helpers";
import { WorkspaceMember } from "@dashboard/workspaces/types";
import { Box, Typography } from "@mui/material";
import { ExpandToggleButton } from "@shared/ExpandToggleButton";
import { MinimalTableCell, MinimalTableRow } from "@shared/table/MinimalTableComponents";
import { Tooltip } from "@shared/Tooltip";

import { useCurrentWorkspace } from "../../api/helpers";

import { RemoveWorkspaceMemberButton } from "./RemoveWorkspaceMemberButton/RemoveWorkspaceMemberButton";
import { ChangeGroupRoleField } from "./ChangeGroupRoleField";
import { ChangeWorkspaceRoleField } from "./ChangeWorkspaceRoleField";
import { useStyles } from "./MemberListItem.style";
import { RemoveGroupMemberButton } from "./RemoveGroupMemberButton";

type Props = {
    index: number;
    member: WorkspaceMember;
};

export const MemberListItem = (props: Props) => {
    const [dialogOpen, setDialogOpen] = React.useReducer((state) => !state, false);

    const { t } = useTranslation(["common", "pages", "modals"]);
    const { classes, cx } = useStyles();

    const groups = useGroupsByMemberEmail(props.member.email);

    const { data: workspace } = useCurrentWorkspace();
    const isWorkspaceMember = workspace?.members.some((member) => member.email === props.member.email);

    const groupsMembers = useGroupsAndWorkspaceMembers();
    const isGuestMember = groupsMembers.some((member) => member.email === props.member.email);

    if (!isWorkspaceMember && !isGuestMember) {
        return null;
    }

    return (
        <React.Fragment>
            <MinimalTableRow>
                <MinimalTableCell className={classes.chevronBox} align="justify">
                    {groups.length ? <ExpandToggleButton expanded={dialogOpen} onClick={setDialogOpen} /> : null}
                </MinimalTableCell>

                <MinimalTableCell align="justify">
                    <Box className={classes.nameContainer}>
                        <Typography>{props.member?.email ?? "-"} </Typography>
                        {props.member.status === "Pending" && (
                            <Tooltip title={t("pages:workspacePage.tooltip.pendingMemberWorkspaceInvitation")}>
                                <Typography variant="body1" className={classes.statusLabel}>
                                    {t("common:status.pending")}
                                </Typography>
                            </Tooltip>
                        )}
                    </Box>
                </MinimalTableCell>

                <MinimalTableCell align="justify">
                    {isWorkspaceMember ? (
                        <ChangeWorkspaceRoleField member={props.member} />
                    ) : (
                        <Typography>{t("pages:workspacePage.table.item.directMemberOfWorkspaceGroup")}</Typography>
                    )}
                </MinimalTableCell>

                <MinimalTableCell align="right">
                    <div className={classes.buttonBox}>
                        <RemoveWorkspaceMemberButton member={props.member} />
                    </div>
                </MinimalTableCell>
            </MinimalTableRow>

            <MinimalTableRow style={{ display: dialogOpen && groups.length ? "table-row" : "none" }}>
                <MinimalTableCell className={classes.chevronBox} />

                <MinimalTableCell className={classes.compactCell}>
                    {groups.map((group) => (
                        <Box key={group.id} className={cx(classes.groupRow, classes.groupName)}>
                            <Typography>{group.name}</Typography>
                        </Box>
                    ))}
                </MinimalTableCell>

                <MinimalTableCell className={classes.compactCell}>
                    {groups.map((group) => (
                        <Box key={group.id} className={classes.groupRow}>
                            <ChangeGroupRoleField group={group} member={props.member} />
                        </Box>
                    ))}
                </MinimalTableCell>

                <MinimalTableCell className={classes.compactCell}>
                    {groups.map((group) => (
                        <Box key={group.id} className={classes.groupRowEnd}>
                            <RemoveGroupMemberButton group={group} member={props.member} />
                        </Box>
                    ))}
                </MinimalTableCell>
            </MinimalTableRow>
        </React.Fragment>
    );
};
